import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import * as api from '../services/apiService';

/**
 * UserInbox - Reddit-style in-app inbox for user notifications
 * 
 * Features:
 * - Shows notifications about user's submissions
 * - Filter by type (All, Approvals, Rejections, Info Requests)
 * - Mark as read (individual or bulk)
 * - Click to view submission details
 * - Auto-refresh every 30 seconds
 * - Red badge showing unread count
 * 
 * Notification Types:
 * - approval: Tag was approved (+points)
 * - rejection: Tag was rejected (with reason)
 * - info_request: Admin needs more information
 * - reminder: Pending action reminder
 * - badge_earned: Achievement unlocked
 * - weekly_summary: Weekly progress report
 */

const UserInbox = ({ isOpen, onClose, userEmail, onReadUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  // Auto-fetch on open
  useEffect(() => {
    if (isOpen && userEmail) {
      fetchNotifications();
    }
  }, [isOpen, userEmail, filter]);

  // Auto-refresh every 30 seconds when open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      fetchNotifications(true); // Silent refresh
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  const fetchNotifications = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      // Debug: Check if userEmail is valid
      if (!userEmail || !userEmail.includes('@')) {
        setLoading(false);
        return;
      }

      const data = await api.fetchUserNotifications(userEmail, {
        limit: 50,
        typeFilter: filter
      });
      setNotifications(data.notifications || []);
      
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );

      // 🔔 Notify parent to update badge count immediately
      if (onReadUpdate) {
        onReadUpdate();
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.markAllNotificationsRead(userEmail);

      // Refresh notifications
      fetchNotifications();
      
      // 🔔 Notify parent to update badge count immediately
      if (onReadUpdate) {
        onReadUpdate();
      }
    } catch (error) {
      console.error('❌ Error marking all as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    
    // Navigate (preferred: explicit action_url; fallback: submission route)
    try {
      const actionUrl = (notification.action_url || '').trim();
      if (actionUrl) {
        onClose();
        // In-app navigation for relative URLs; external URLs open new tab.
        if (actionUrl.startsWith('/')) {
          navigate(actionUrl);
        } else {
          window.open(actionUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      if (notification.inspection_id) {
        onClose();
        navigate(`/submission/${notification.inspection_id}`);
      }
    } catch (e) {
      // Best-effort only; keep inbox usable even if a bad URL sneaks in.
    }
  };

  // Notification type metadata
  const typeMetadata = {
    approval: { icon: '🎉', color: 'green', label: 'Approved', bgClass: 'bg-green-500/10 border-green-500/30' },
    rejection: { icon: '❌', color: 'red', label: 'Rejected', bgClass: 'bg-red-500/10 border-red-500/30' },
    info_request: { icon: '📝', color: 'yellow', label: 'Info Needed', bgClass: 'bg-yellow-500/10 border-yellow-500/30' },
    reminder: { icon: '⏰', color: 'blue', label: 'Reminder', bgClass: 'bg-blue-500/10 border-blue-500/30' },
    badge_earned: { icon: '🏆', color: 'purple', label: 'Badge', bgClass: 'bg-purple-500/10 border-purple-500/30' },
    weekly_summary: { icon: '📊', color: 'gray', label: 'Summary', bgClass: 'bg-gray-500/10 border-gray-500/30' },
    team_report: { icon: '📊', color: 'blue', label: 'Team Report', bgClass: 'bg-[var(--pbfs-blue)]/10 border-[var(--pbfs-blue)]/30' },
    new_lead: { icon: '🔥', color: 'orange', label: 'New Lead', bgClass: 'bg-orange-500/10 border-orange-500/30' },
    unassigned_lead: { icon: '⚠️', color: 'red', label: 'Needs Assignment', bgClass: 'bg-red-500/10 border-red-500/30' },
    low_confidence_scan: { icon: '⚠️', color: 'yellow', label: 'Review Needed', bgClass: 'bg-yellow-500/10 border-yellow-500/30' }
  };

  const formatTimeAgo = (timestamp) => {
    try {
      const now = new Date();
      const then = new Date(timestamp);
      const diffMs = now - then;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      
      const diffWeeks = Math.floor(diffDays / 7);
      return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    } catch {
      return timestamp;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter(n => n.type === filter);

  if (!isOpen) return null;

  const modal = (
    <div 
      className="fixed inset-0 z-[100000] flex items-center justify-center p-4 pt-20 pb-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[var(--pbfs-navy)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border-2 border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              Inbox
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-sm font-bold px-2.5 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h2>
            <p className="text-white/60 text-sm mt-1">
              Notifications about your submissions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none transition-colors"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="p-4 border-b border-white/10 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === 'all'
                ? 'bg-[var(--pbfs-blue)] text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            📋 All ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('approval')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === 'approval'
                ? 'bg-[var(--pbfs-blue)] text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            ✅ Approvals ({notifications.filter(n => n.type === 'approval').length})
          </button>
          <button
            onClick={() => setFilter('rejection')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === 'rejection'
                ? 'bg-[var(--pbfs-blue)] text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            ❌ Rejections ({notifications.filter(n => n.type === 'rejection').length})
          </button>
          <button
            onClick={() => setFilter('info_request')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === 'info_request'
                ? 'bg-[var(--pbfs-blue)] text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            📝 Info Requests ({notifications.filter(n => n.type === 'info_request').length})
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-white/60">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-white/60 text-lg font-semibold mb-2">No notifications yet</p>
              <p className="text-white/40 text-sm">
                {filter === 'all'
                  ? "You'll see updates about your submissions here"
                  : `No ${typeMetadata[filter]?.label || filter} notifications`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => {
                const meta = typeMetadata[notification.type] || { icon: '📬', color: 'gray', bgClass: 'bg-gray-500/10 border-gray-500/30' };
                const isUnread = !notification.read;
                
                return (
                  <div
                    key={notification.id}
                    className={`
                      rounded-lg p-4 border transition-all cursor-pointer
                      ${isUnread ? `${meta.bgClass} border-2` : 'bg-white/5 border-white/10'}
                      hover:bg-white/10
                    `}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="text-3xl flex-shrink-0">
                        {meta.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className={`text-sm font-semibold ${isUnread ? 'text-white' : 'text-white/70'}`}>
                            {notification.title}
                          </h3>
                          {isUnread && (
                            <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        
                        <p className="text-white/60 text-sm mb-2">
                          {notification.message}
                        </p>
                        
                        {notification.metadata?.business_name && (
                          <p className="text-white/40 text-xs mb-1">
                            📍 {notification.metadata.business_name}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-white/40 text-xs">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          
                          {notification.metadata?.points_awarded && (
                            <span className="text-[var(--pbfs-green)] text-xs font-semibold">
                              +{notification.metadata.points_awarded} points
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-between items-center">
          {unreadCount > 0 ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
              className="text-white/60 hover:text-white text-sm font-semibold transition-colors"
            >
              ✓ Mark All as Read
            </button>
          ) : (
            <div></div>
          )}
          
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[var(--pbfs-blue)] text-white rounded-lg hover:bg-[var(--pbfs-blue)]/80 transition-colors font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default UserInbox;
